import React from 'react'
import Loader from '../../../components/common/loader'
import { formateDateWithTime } from '../../../helpers/commonFunctions'
import { formatLabel, normalizeKey } from './jobDisplay'

/**
 * What the counts mean, in words. Every queue is removeOnComplete, so a zero
 * completed count is never evidence that nothing ran — each queue can reword
 * the sentences without rebuilding the block.
 */
export const QueueStatsNotes = ({ stats, depthText, failedText, completedText }) => {
    const depth = Number(stats?.waiting || 0) + Number(stats?.active || 0)
    const failed = Number(stats?.failed || 0)
    return (
        <div className="d-flex flex-column" style={{ gap: 6 }}>
            {depth > 0 ? (
                <div className="alert alert-info mb-0 py-2">
                    <b>{depth}</b> {depthText || 'job(s) still queued or running.'}
                </div>
            ) : null}
            {failed > 0 ? (
                <div className="alert alert-danger mb-0 py-2">
                    <b>{failed}</b> {failedText || 'job(s) failed.'} Fix the cause, then use <b>Retry All Failed</b> below and watch this count fall to zero.
                </div>
            ) : null}
            <small className="text-muted">
                {completedText || 'Completed stays at or near zero by design — successful jobs are removed from the queue as soon as they finish, so it does not mean nothing has run.'}
            </small>
        </div>
    )
}

/**
 * The chrome every queue screen shares: the stats card with its nine actions and
 * the failed-jobs card. A queue component supplies only its own columns, its own
 * row renderer, and anything extra it wants under the tiles.
 *
 * @param queueState   the object returned by useQueue
 * @param title        heading for the stats card
 * @param pollMs       the queue's poll interval, for the "updated at" stamp
 * @param statsFooter  node under the tiles; defaults to <QueueStatsNotes />
 * @param columns      [{ label, width }] for the failed table head
 * @param renderRow    (job, index) => the row's <td> cells; the panel owns the
 *                     <tr> and the leading select checkbox
 * @param showCleanCompleted  false on queues that remove jobs on completion,
 *                     where the button would always clean nothing
 */
const QueuePanel = ({ queueState, title, pollMs = 0, statsFooter = null, columns = [], renderRow, showCleanCompleted = true }) => {

    const {
        stats, statsLoader, statsUpdatedAt,
        failedJobs, failedLoader,
        failedLimit, setFailedLimit,
        refresh, fetchFailed,
        selectedIds, isSelected, toggleJob, toggleAll, allSelected,
        handleRetryFailed, handleRetrySelected, handleCleanCompleted, handleCleanFailed,
        handleBackup, handleClear, handlePause, handleResume,
    } = queueState

    return (
        <>
            {/* Queue Stats */}
            <div className="card custom-card overflow-hidden mb-3">
                <div className="card-body">
                    <div className='d-flex justify-content-between align-items-center mb-4 flex-wrap'>
                        <h6 className="main-content-label mb-0">
                            Queue Stats: {title}
                            {pollMs && statsUpdatedAt ?
                                <small className="text-muted ms-2" style={{ fontWeight: 400 }}>
                                    updated {formateDateWithTime(statsUpdatedAt)} · auto-refreshes every {pollMs / 1000}s
                                </small> : null}
                        </h6>
                        <div className="d-flex flex-wrap gap-2">
                            <button className="btn ripple btn-main-primary signbtn me-2" onClick={refresh}>Refresh</button>
                            <button className="btn ripple btn-info me-2" onClick={handlePause}>Pause</button>
                            <button className="btn ripple btn-success me-2" onClick={handleResume}>Resume</button>
                            <button className="btn ripple btn-secondary me-2" onClick={handleBackup}>Backup</button>
                            {showCleanCompleted ?
                                <button className="btn ripple btn-warning me-2" onClick={handleCleanCompleted}>Clean Completed</button>
                                : null}
                            <button className="btn ripple btn-danger" onClick={handleClear}>Clear Queue</button>
                        </div>
                    </div>
                    {statsLoader ? <Loader /> : stats ? (<>
                        <div className="row">
                            {Object.entries(stats).map(([key, value]) => {
                                // Failed is the only count that asks for action, so it carries the alarm.
                                const isFailedCount = normalizeKey(key) === 'failed' && Number(value) > 0
                                return (
                                    <div className="col-lg-2 col-md-4 col-sm-6 mb-3" key={key}>
                                        <div className={`card border p-3 text-center${isFailedCount ? ' border-danger' : ''}`}>
                                            <h6 className={`mb-1 ${isFailedCount ? 'text-danger' : 'text-muted'}`}>{formatLabel(key)}</h6>
                                            <h4 className={isFailedCount ? 'text-danger mb-0' : 'mb-0'}>{value != null ? String(value) : 'N/A'}</h4>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        {statsFooter || <QueueStatsNotes stats={stats} />}
                    </>) : <p className="text-center text-muted">No stats available</p>}
                </div>
            </div>

            {/* Failed Jobs */}
            <div className="card custom-card overflow-hidden mb-3">
                <div className="card-body">
                    <div className='d-flex justify-content-between align-items-center mb-4 flex-wrap'>
                        <h6 className="main-content-label mb-0">Failed Jobs</h6>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                            <label className="me-2 mb-0">Limit:</label>
                            <input
                                type="number"
                                min="1"
                                max="200"
                                className="form-control me-2"
                                style={{ width: '100px' }}
                                value={failedLimit}
                                onChange={(e) => setFailedLimit(Number(e.target.value) || 20)}
                            />
                            <button className="btn ripple btn-main-primary signbtn me-2" onClick={fetchFailed}>Apply</button>
                            {selectedIds.length ?
                                <button className="btn ripple btn-success me-2" onClick={handleRetrySelected}>
                                    Retry Selected ({selectedIds.length})
                                </button> : null}
                            <button className="btn ripple btn-success me-2" onClick={handleRetryFailed}>Retry All Failed</button>
                            <button className="btn ripple btn-danger" onClick={handleCleanFailed}>Clean Failed</button>
                        </div>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-bordered border-t0 key-buttons text-nowrap w-100">
                            <thead>
                                <tr>
                                    <th className="queue-select-cell">
                                        <input
                                            type="checkbox"
                                            className="queue-select-checkbox"
                                            aria-label="Select all listed jobs"
                                            title={allSelected ? 'Clear selection' : 'Select all listed jobs'}
                                            checked={allSelected}
                                            disabled={!failedJobs.length}
                                            onChange={toggleAll}
                                        />
                                    </th>
                                    {columns.map((column) => (
                                        <th key={column.label} width={column.width}>{column.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {failedLoader ?
                                    <tr>
                                        <td colSpan={columns.length + 1}><Loader /></td>
                                    </tr> : <>
                                        {failedJobs.length
                                            ? failedJobs.map((row, i) => (
                                                <tr key={row.id || i} className={isSelected(row.id) ? 'queue-row-selected' : ''}>
                                                    <td className="queue-select-cell">
                                                        <input
                                                            type="checkbox"
                                                            className="queue-select-checkbox"
                                                            aria-label={`Select job ${row.id}`}
                                                            checked={isSelected(row.id)}
                                                            onChange={() => toggleJob(row.id)}
                                                        />
                                                    </td>
                                                    {renderRow(row, i)}
                                                </tr>
                                            ))
                                            : <tr><td colSpan={columns.length + 1} className="text-center">No failed jobs</td></tr>}
                                    </>}
                            </tbody>
                        </table>
                    </div>
                    <div role="status" aria-live="polite"><b>Total Records : {failedJobs.length || 0}</b></div>
                </div>
            </div>
        </>
    )
}

export default QueuePanel
